import React, { useEffect, useState } from "react";
import "./App.css";
import Header from "./components/Header/Header";
import ItemsList from "./components/ItemsList/ItemsList";
import Footer from "./components/Footer/Footer";
import Modal from "react-modal";
import transition from "./pics/transition.png";
import transition2 from "./pics/transition2.png";
import OutfitsList from "./components/OutfitsList/OutfitsList";
import { serverUrl } from "./constants";
import { UserDb } from "./models/user.db";
import { ItemDb } from "./models/item.db";
import { OutfitDb } from "./models/outfit.db";
import { PortfolioDb } from "./models/portfolio.db";
import PortfoliosList from "./components/PortfoliosList/PortfoliosList";
import { getSortingVal } from "./utils/sorting";
import { OcModat } from "./OcModal";
import useAuth from "./hooks/useAuth";
import { isDefined } from "./utils/objects";
import { logEv } from "./services/firebase";
import { CollectionDb } from "./models/collection.db";
import { CollectionItemDb } from "./models/collection-item.db";
import { CollectionOutfitDb } from "./models/collection-outfit.db";
import { CollectionLinkDb } from "./models/collectio-link.db";
import { InspirationDb } from "./models/inspiration.db";
import CollectionsList from "./components/CollectionsList/CollectionsList";
import { useLocation, useNavigate } from "react-router";
import useWindowSize from "./hooks/useWindowSize";

interface OpenClosetResponse {
  status: string;
  user: UserDb | null;
  items: Array<ItemDb>;
  outfits: Array<OutfitDb>;
  portfolios: Array<PortfolioDb>;
  collections: Array<CollectionDb>;
  collectionItems: Array<CollectionItemDb>;
  collectionOutfits: Array<CollectionOutfitDb>;
  collectionInspirations: Array<CollectionLinkDb>;
  inspirations: Array<InspirationDb>;
}

function useOpenCloset(userName: string) {
  const [data, setData] = useState<OpenClosetResponse>({
    user: null,
    items: [],
    outfits: [],
    portfolios: [],
    collections: [],
    collectionItems: [],
    collectionOutfits: [],
    collectionInspirations: [],
    inspirations: [],
    status: "LOADING",
  });
  useEffect(() => {
    const getData = async () => {
      if (!userName) {
        setData({
          user: null,
          items: [],
          outfits: [],
          portfolios: [],
          collections: [],
          collectionItems: [],
          collectionOutfits: [],
          collectionInspirations: [],
          inspirations: [],
          status: "ERROR",
        });
        return;
      }
      const res = await fetch(`${serverUrl}/users/v2/openCloset`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: userName,
          useCollections: true,
        }),
      });
      const json = await res.json();
      if (json.items) {
        json.items.sort((a: ItemDb, b: ItemDb) =>
          getSortingVal(b.sFrac, b.createdAt).localeCompare(
            getSortingVal(a.sFrac, a.createdAt)
          )
        );
      }
      if (json.outfits) {
        json.outfits.sort((a: OutfitDb, b: OutfitDb) =>
          getSortingVal(b.sFrac, b.createdAt).localeCompare(
            getSortingVal(a.sFrac, a.createdAt)
          )
        );
      }
      if (json.portfolios) {
        json.portfolios.sort((a: PortfolioDb, b: PortfolioDb) =>
          getSortingVal(null, b.createdAt).localeCompare(
            getSortingVal(null, a.createdAt)
          )
        );
      }
      if (json.collections) {
        json.collections.sort((a: CollectionDb, b: CollectionDb) =>
          getSortingVal(b.sFrac, b.createdAt).localeCompare(
            getSortingVal(a.sFrac, a.createdAt)
          )
        );
      }
      setData({
        user: json.user,
        items: json.items ?? [],
        outfits: json.outfits ?? [],
        portfolios: json.portfolios ?? [],
        collections: json.collections ?? [],
        collectionItems: json.collectionItems ?? [],
        collectionOutfits: json.collectionOutfits ?? [],
        collectionInspirations: json.collectionInspirations ?? [],
        inspirations: json.inspirations ?? [],
        status: json.status,
      });
    };
    try {
      getData();
    } catch (e) {
      setData({
        user: null,
        items: [],
        outfits: [],
        portfolios: [],
        collections: [],
        collectionItems: [],
        collectionOutfits: [],
        collectionInspirations: [],
        inspirations: [],
        status: "ERROR",
      });
    }
  }, [userName]);
  return { data };
}

function App() {
  const size = useWindowSize();
  const isMobile = size.width <= 800;
  const buttonFontSize = isMobile ? 12 : 18;
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const pathArray = path.split("/");
  const isUser = pathArray[1] === "user";
  const username = isUser ? pathArray[2] || "" : "";
  const isItemsTab = pathArray[3] && pathArray[3] === "items";
  const isOutfitsTab = pathArray[3] && pathArray[3] === "outfits";
  const isCollectionsTab = pathArray[3] && pathArray[3] === "collections";
  const collectionId = isCollectionsTab && pathArray[4] ? pathArray[4] : null;
  const isPortfoliosTab = pathArray[3] && pathArray[3] === "portfolios";
  const startIndex = isOutfitsTab
    ? 0
    : isItemsTab
    ? 1
    : isPortfoliosTab
    ? 2
    : isCollectionsTab
    ? 3
    : 0;
  const { data } = useOpenCloset(username);
  const status = data?.status;
  const userDb = data?.user;
  const items = data?.items ?? [];
  const noWishItems = items.filter((item) => item.wishlist !== true);
  const outfits = data?.outfits ?? [];
  const portfolios = data?.portfolios ?? [];
  const collections = data?.collections ?? [];
  const collectionItems = data?.collectionItems ?? [];
  const collectionOutfits = data?.collectionOutfits ?? [];
  const collectionInspirations = data?.collectionInspirations ?? [];
  const inspirations = data?.inspirations ?? [];

  const [topIndex, setTopIndex] = React.useState(startIndex);

  useEffect(() => {
    setTopIndex(startIndex);
  }, [path, startIndex]);

  const [modalIsOpen, setIsOpen] = React.useState(false);
  const { fbUser, indyxUser, logOut, updateUser } = useAuth();

  const okIndyxUser = !!(fbUser && indyxUser);
  const okIndyxData =
    isDefined(indyxUser?.firstName) &&
    isDefined(indyxUser?.lastName) &&
    isDefined(indyxUser?.socialMediaSource) &&
    isDefined(indyxUser?.goals) &&
    isDefined(indyxUser?.sourceAttributionText);

  useEffect(() => {
    if (okIndyxUser && !okIndyxData) {
      setIsOpen(true);
    }
  }, [okIndyxUser, okIndyxData]);

  useEffect(() => {
    logEv("open_closet_view", { username: username });
  }, [username]);

  function openModal1() {
    setIsOpen(true);
  }

  function openModal2() {
    setIsOpen(true);
  }

  function afterOpenModal() {
    // references are now sync'd and can be accessed.
    //subtitle.style.color = '#f00';
  }

  function closeModal() {
    setIsOpen(false);
  }

  return (
    <div className="App">
      {status === "OK" ? (
        <div className="wrapper">
          <Header logoUrl={`/icons/logo.png`} user={userDb} />
          <div
            style={{
              cursor: "pointer",
              height: 40,
              display: "flex",
              flexDirection: "row",
              background: "rgba(245, 241, 229, 1)",
              borderTopWidth: 0.5,
              borderTopColor: "black",
              borderTopStyle: "solid",
            }}
          >
            <div
              onClick={() => {
                navigate(`/user/${username}/outfits`);
              }}
              style={{
                fontFamily: "Mabry",
                cursor: "pointer",
                height: 40,
                width: "50vw",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                //
                borderRightWidth: 0.5,
                borderRightColor: "black",
                borderRightStyle: "solid",
                backgroundColor:
                  topIndex === 0 ? "#C6BCA9" : "rgba(245, 241, 229, 1)",
                fontSize: buttonFontSize,
                whiteSpace: "pre-line",
              }}
            >
              {`OUTFITS${isMobile ? "\n" : " "}(${outfits.length})`}
            </div>

            <div
              onClick={() => {
                navigate(`/user/${username}/items`);
              }}
              style={{
                fontFamily: "Mabry",
                height: 40,
                width: "50vw",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor:
                  topIndex === 1 ? "#C6BCA9" : "rgba(245, 241, 229, 1)",
                fontSize: buttonFontSize,
                whiteSpace: "pre-line",
              }}
            >
              {`ITEMS${isMobile ? "\n" : " "}(${noWishItems.length})`}
            </div>
            {collections.length > 0 ? (
              <div
                onClick={() => {
                  navigate(`/user/${username}/collections`);
                }}
                style={{
                  fontFamily: "Mabry",
                  height: 40,
                  width: "50vw",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderLeftWidth: 0.5,
                  borderLeftColor: "black",
                  borderLeftStyle: "solid",
                  backgroundColor:
                    topIndex === 3 ? "#C6BCA9" : "rgba(245, 241, 229, 1)",
                  fontSize: buttonFontSize,
                  whiteSpace: "pre-line",
                }}
              >
                {`COLLECTIONS${isMobile ? "\n" : " "}(${collections.length})`}
              </div>
            ) : null}
            {portfolios.length > 0 ? (
              <div
                onClick={() => {
                  navigate(`/user/${username}/portfolios`);
                }}
                style={{
                  fontFamily: "Mabry",
                  height: 40,
                  width: "50vw",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderLeftWidth: 0.5,
                  borderLeftColor: "black",
                  borderLeftStyle: "solid",
                  backgroundColor:
                    topIndex === 2 ? "#C6BCA9" : "rgba(245, 241, 229, 1)",
                  fontSize: buttonFontSize,
                  whiteSpace: "pre-line",
                }}
              >
                {`PORTFOLIO${isMobile ? "\n" : " "}(${portfolios.length})`}
              </div>
            ) : null}
          </div>
          {topIndex === 0 ? (
            <OutfitsList
              outfits={outfits}
              onShowModal={openModal1}
              emptyString="There are no outfits"
            />
          ) : topIndex === 1 ? (
            <ItemsList
              items={noWishItems}
              onShowModal={openModal1}
              emptyString="There are no items"
            />
          ) : topIndex === 2 ? (
            <PortfoliosList
              items={portfolios}
              onShowModal={openModal1}
              emptyString="There are no portfolios"
            />
          ) : (
            <CollectionsList
              userId={username}
              collectionId={collectionId}
              data={{
                items: items,
                outfits: outfits,
                collections: collections,
                collectionItems: collectionItems,
                collectionOutfits: collectionOutfits,
                collectionInspirations: collectionInspirations,
                inspirations: inspirations,
              }}
              onShowModal={openModal1}
              emptyString="Oops, there are no collections."
            />
          )}
          <Footer user={userDb} onDownload={openModal2} />
          <Modal
            isOpen={modalIsOpen}
            onAfterOpen={afterOpenModal}
            onRequestClose={closeModal}
            style={{
              content: {
                top: "50%",
                left: "50%",
                right: "auto",
                bottom: "auto",
                marginRight: "-50%",
                transform: "translate(-50%, -50%)",
                padding: "0",
                background: "transparent",
                border: "none",
              },
            }}
            contentLabel="Download Modal"
          >
            <OcModat
              closeModal={closeModal}
              logOut={logOut}
              updateUser={updateUser}
              ocUser={userDb}
              indyxUser={indyxUser}
              fbUser={fbUser}
            />
          </Modal>
        </div>
      ) : null}
      {status === "ERROR" ? (
        <div
          style={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "start",
            justifyContent: "center",
            fontSize: 40,
            flexDirection: "column",
            backgroundImage: `url(${transition2})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "100%",
            backgroundPosition: "center",
          }}
        >
          <div
            style={{
              fontSize: 30,
              paddingLeft: 40,
              paddingRight: 40,
              paddingBottom: 20,
              fontFamily: "Spezia",
            }}
          >
            Oops...
          </div>
          <div
            style={{
              fontSize: 16,
              textAlign: "left",
              paddingLeft: 40,
              paddingRight: 40,
              fontFamily: "Spezia",
            }}
          >
            This closet is private and can’t be displayed.
            <br />
            <br />
            If you were invited into this closet, please contact the owner so
            they can update their privacy settings.
            <br />
            <br />
            If you think something else is wrong, send us a note at
            help@myindyx.com.
          </div>
        </div>
      ) : null}
      {status === "LOADING" ? (
        <div
          style={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 40,
            backgroundImage: `url(${transition})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "100%",
            backgroundPosition: "center",
          }}
        >
          LOADING...
        </div>
      ) : null}
    </div>
  );
}

export default App;
